/**
 * Engine-independent stream-quality observations that ride `clientStats`.
 *
 * Observability only: nothing here feeds the congestion ladder or `signalAvailabilityFor`.
 * These fields exist because the per-engine capability matrix withholds Safari's inbound-rtp
 * counters from the ladder, which left a Safari session with no measured account of what
 * reached the glass. Every field is omitted, never zero-filled, when the engine did not report it.
 */

/** What the page observed being presented this interval (rVFC + getVideoPlaybackQuality). */
export type PresentationSummary = {
  /** Wall span this summary covers, ms. */
  elapsedMs: number;
  /** requestVideoFrameCallback invocations: frames the page saw presented. */
  callbacks: number;
  /** callbacks x 1000 / elapsedMs. */
  presentedFps: number;
  /** Inter-presentation gaps at or above PRESENT_STALL_GAP_MS this interval. */
  stallCount: number;
  /** Largest gap between consecutive presentations this interval, ms. */
  maxPresentGapMs?: number;
  /** Time since the last presented frame at summary time, ms: a freeze shows here while rVFC is silent. */
  sinceLastPresentMs?: number;
  /** Delta of rVFC metadata.presentedFrames: the compositor's own count, including frames rVFC missed. */
  compositorPresentedDelta?: number;
  /** Delta of getVideoPlaybackQuality().droppedVideoFrames. */
  playbackDroppedDelta?: number;
  /** Delta of getVideoPlaybackQuality().totalVideoFrames. */
  playbackTotalDelta?: number;
};

/** Video inbound-rtp counter deltas over the interval; a key is present only when the engine reports it. */
export type ReceiverCounterSummary = {
  framesDecoded?: number;
  framesDropped?: number;
  keyFramesDecoded?: number;
  nacks?: number;
  plis?: number;
  firs?: number;
  retransmittedPackets?: number;
  /** Packets that arrived too late (or duplicated) and were discarded by the jitter buffer. */
  packetsDiscarded?: number;
  freezes?: number;
  freezeMs?: number;
  pauses?: number;
};

/** The inbound-rtp stat keys this engine actually reports. Sent once per connection. */
export type StatKeyInventory = { video: string[]; audio: string[] };

export const RECEIVER_COUNTER_KEYS: ReadonlyArray<keyof ReceiverCounterSummary> = [
  'framesDecoded',
  'framesDropped',
  'keyFramesDecoded',
  'nacks',
  'plis',
  'firs',
  'retransmittedPackets',
  'packetsDiscarded',
  'freezes',
  'freezeMs',
  'pauses',
];

/** Upper bound per list so a malformed report cannot balloon the wire message. */
export const STAT_KEY_INVENTORY_MAX_KEYS = 200;

function isNonNegativeFinite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isOptionalNonNegativeFinite(value: unknown): boolean {
  return value === undefined || isNonNegativeFinite(value);
}

export function isPresentationSummary(value: unknown): value is PresentationSummary {
  if (value === null || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  if (!isNonNegativeFinite(s['elapsedMs']) || !isNonNegativeFinite(s['callbacks'])) return false;
  if (!isNonNegativeFinite(s['presentedFps']) || !isNonNegativeFinite(s['stallCount'])) return false;
  return [
    'maxPresentGapMs',
    'sinceLastPresentMs',
    'compositorPresentedDelta',
    'playbackDroppedDelta',
    'playbackTotalDelta',
  ].every((key) => isOptionalNonNegativeFinite(s[key]));
}

export function isReceiverCounterSummary(value: unknown): value is ReceiverCounterSummary {
  if (value === null || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  return RECEIVER_COUNTER_KEYS.every((key) => isOptionalNonNegativeFinite(s[key]));
}

function isKeyList(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= STAT_KEY_INVENTORY_MAX_KEYS &&
    value.every((key) => typeof key === 'string')
  );
}

export function isStatKeyInventory(value: unknown): value is StatKeyInventory {
  if (value === null || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  return isKeyList(s['video']) && isKeyList(s['audio']);
}
